'use client';

import { c as _c } from "react/compiler-runtime";
import { memo } from 'react';
import { useTranslations } from 'next-intl';
import { ResponsiveImageDataWithBadges } from '@/app/[abTest]/[locale]/(spreadshirt)/shop/design/components/GalleryImage';
import { GalleryBadgeLabels, getHeroImageBadges } from '@/app/[abTest]/[locale]/(spreadshirt)/shop/design/utility/galleryBadges';
import { useResolvedAppearances } from '@/components/ProductCard/AppearanceConfigProvider';
import { FEATURE_SUSTAINABLE_ID, MATERIAL_VIEW_ID } from '@/utility/constants';
import { getProductTypeImagesByTagHierarchy } from '@/utility/productTypeImages';
import { getProductTypeImageAltText } from '@/utility/seo';
import ImageGallery from '../ImageGallery';
type CyoImageGallery = {
  productType: ClientProductType;
  modelImages: ModelImage[] | undefined;
  appearanceId: string;
  isSpreadBrand?: boolean;
};
export const CyoImageGallery = memo(t0 => {
  const $ = _c(16);
  const {
    productType,
    modelImages,
    appearanceId,
    isSpreadBrand: t1
  } = t0;
  const isSpreadBrand = t1 === undefined ? false : t1;
  const t = useTranslations();
  let t2;
  if ($[0] !== appearanceId) {
    t2 = [appearanceId];
    $[0] = appearanceId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [appearance] = useResolvedAppearances(t2);
  let t3;
  if ($[2] !== appearance?.name || $[3] !== appearanceId || $[4] !== isSpreadBrand || $[5] !== modelImages || $[6] !== productType || $[7] !== t) {
    const galleryImages = getProductTypeImagesByTagHierarchy(productType, modelImages, appearanceId, "detail");
    const badgeLabels = {
      sortNew: t("filter.sortNew"),
      sustainable: t("filter.sustainable"),
      embroidery: t("filter.embroidery"),
      ourPick: t("mpDetailPage.ourPickBadge")
    };
    const badgeFlags = {
      isSpreadBrand,
      isSustainable: productType.labels?.features.some(_temp)
    };
    let t4;
    if ($[9] !== productType.views) {
      t4 = productType.views.some(_temp2);
      $[9] = productType.views;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    const hasMaterialView = t4;
    let t5;
    if ($[11] !== appearanceId || $[12] !== hasMaterialView || $[13] !== productType.id || $[14] !== productType.name) {
      t5 = hasMaterialView ? [{
        type: "productType",
        productTypeId: productType.id,
        viewId: MATERIAL_VIEW_ID,
        appearanceId,
        loading: "lazy",
        alt: productType.name
      } satisfies ResponsiveImageDataWithBadges] : [];
      $[11] = appearanceId;
      $[12] = hasMaterialView;
      $[13] = productType.id;
      $[14] = productType.name;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t3 = <ImageGallery spacingFactor={28} images={[...galleryImages.map((image, index) => ({
      type: "productType",
      productTypeId: productType.id,
      viewId: image.viewId,
      appearanceId,
      modelId: image.modelId,
      crop: image.crop,
      loading: index === 0 ? "eager" : "lazy",
      priority: index === 0 ? "high" : "auto",
      alt: getProductTypeImageAltText(productType.name, appearance?.name ?? "", productType.views.find(v => v.id === image.viewId)?.name, image.model?.persons),
      badges: index === 0 ? getHeroImageBadges(badgeFlags, badgeLabels, {
        showOurPickOnHeroImage: true
      }) : undefined
    }) satisfies ResponsiveImageDataWithBadges), ...t5]} />;
    $[2] = appearance?.name;
    $[3] = appearanceId;
    $[4] = isSpreadBrand;
    $[5] = modelImages;
    $[6] = productType;
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
});
CyoImageGallery.displayName = 'CyoImageGallery';
function _temp(label) {
  return label.id === FEATURE_SUSTAINABLE_ID;
}
function _temp2(view) {
  return view.id === MATERIAL_VIEW_ID;
}